/* Monta as séries do gráfico "execução por tipo de atividade".

   Existe separado do TurmaPainel porque é a única parte do painel em que o
   NÚMERO de séries vem do dado, e não do código — e é aí que a paleta pode
   acabar. A paleta tem quatro cores mutuamente distinguíveis (ver a explicação
   em styles/tokens.css); os tipos além disso somam em "Outros".

   O que havia antes: `var(--color-chart-${(i % 8) + 1})`. Com nove tipos, o 9º
   recebia a cor do 1º e o gráfico passava a mostrar duas séries diferentes como
   se fossem a mesma coisa — o pior tipo de defeito num gráfico, porque não
   parece defeito nenhum.

   Vive em lib/ para poder ser testado sem renderizar o painel. */

export const MAX_SERIES_COM_COR = 4;
export const ROTULO_OUTROS = "Outros";

/** Soma `concluidas` por tipo de atividade. */
function volumePorTipo(porTipo) {
  const volume = new Map();
  for (const r of porTipo) {
    volume.set(
      r.tipo_atividade,
      (volume.get(r.tipo_atividade) ?? 0) + (r.concluidas ?? 0)
    );
  }
  return volume;
}

/**
 * Decide quais tipos ganham cor própria e quais somam em "Outros".
 *
 * O critério é volume total no período, do maior para o menor: quem aparece
 * mais fica com cor própria. Empate resolve pela ordem em que o tipo apareceu
 * no dado — `sort` é estável no Node 11+, então a saída é determinística e o
 * gráfico não troca de cor entre dois carregamentos do mesmo dado.
 *
 * @returns {{comCorPropria: string[], agrupados: string[], tipos: string[]}}
 *   `tipos` é a ordem final das séries, já com "Outros" no fim quando houver.
 */
export function separarTiposPorVolume(porTipo, max = MAX_SERIES_COM_COR) {
  const volume = volumePorTipo(porTipo);
  const porVolume = Array.from(volume.keys()).sort(
    (a, b) => volume.get(b) - volume.get(a)
  );
  const comCorPropria = porVolume.slice(0, max);
  const agrupados = porVolume.slice(max);
  return {
    comCorPropria,
    agrupados,
    tipos: agrupados.length ? [...comCorPropria, ROTULO_OUTROS] : comCorPropria
  };
}

/**
 * Série pronta para o gráfico: uma entrada por tipo, na ordem de volume.
 *
 * "Outros" recebe o cinza neutro de propósito — não é uma categoria a mais e
 * não deve competir visualmente com as que têm nome próprio.
 */
export function seriesDeTipos(tipos) {
  return tipos.map((tipo, i) => ({
    key: tipo,
    label: tipo,
    color:
      tipo === ROTULO_OUTROS
        ? "var(--color-ink-faint)"
        : `var(--color-chart-${i + 1})`
  }));
}

/**
 * Uma linha por período, com o total de concluídas de cada série.
 *
 * Os tipos agrupados são somados na coluna "Outros" — por isso o acumulador
 * começa zerado em todas as séries em vez de buscar a linha com `find`: com
 * agrupamento, mais de um registro do dado cai na mesma coluna.
 */
export function linhasPorPeriodo(porTipo, tipos, agrupados, formatarPeriodo) {
  const periodos = Array.from(new Set(porTipo.map(r => r.periodo))).sort();
  return periodos.map(periodo => {
    const row = { periodo_fmt: formatarPeriodo(periodo) };
    for (const tipo of tipos) row[tipo] = 0;
    for (const r of porTipo) {
      if (r.periodo !== periodo) continue;
      const alvo = agrupados.includes(r.tipo_atividade)
        ? ROTULO_OUTROS
        : r.tipo_atividade;
      row[alvo] += r.concluidas ?? 0;
    }
    return row;
  });
}

/** Texto do subtítulo — o agrupamento nunca é silencioso. */
export function hintDeTipos(agrupados) {
  if (!agrupados.length) return "Concluídas por tipo de atividade";
  const plural = agrupados.length === 1 ? "" : "s";
  return `Concluídas por tipo · ${agrupados.length} tipo${plural} de menor volume somado${plural} em "${ROTULO_OUTROS}"`;
}
